import React, { useEffect, useState } from "react";
import { applicationsApi } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ATS } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const STAGES = [
  { key: "sourced", label: "Sourced" },
  { key: "screened", label: "Screened" },
  { key: "interviewed", label: "Interviewed" },
  { key: "offered", label: "Offered" },
  { key: "placed", label: "Placed" },
  { key: "rejected", label: "Rejected" },
];

export default function ATSPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { setApps(await applicationsApi.list()); }
    finally { setLoading(false); }
  };

  const move = async (id, newStage) => {
    const original = apps;
    setApps(apps.map((a) => a.id === id ? { ...a, stage: newStage } : a));
    try {
      await applicationsApi.moveStage(id, newStage);
      toast.success(`Moved to ${newStage}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move");
      setApps(original);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Applicant Tracking</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Pipeline Board</h1>
        <p className="text-muted-foreground text-sm mt-1">Drag or use the stage selector on any card. AI match scores are advisory — decisions are yours.</p>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading pipeline…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {STAGES.map((s) => {
            const cards = apps.filter((a) => a.stage === s.key);
            return (
              <div key={s.key} data-testid={ATS.kanbanColumn(s.key)} className="min-h-96 bg-[#FAFAFA] border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-xs uppercase tracking-widest font-bold">{s.label}</div>
                  <span className="text-xs font-mono text-muted-foreground">{cards.length}</span>
                </div>
                <div className="space-y-2">
                  {cards.map((a) => (
                    <div key={a.id} data-testid={ATS.kanbanCard(a.id)} className="kanban-card bg-white border border-border rounded-md p-3">
                      <div className="flex items-start gap-2">
                        {a.candidate?.avatar ? (
                          <img src={a.candidate.avatar} className="h-8 w-8 rounded-full object-cover" alt="" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-[#F4F4F5] grid place-items-center text-xs font-bold">{a.candidate?.name?.[0]}</div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{a.candidate?.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{a.candidate?.title}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-1">
                        <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-mono text-[10px] tracking-wider">
                          <Sparkles className="h-2.5 w-2.5 mr-1" />{a.match_score}%
                        </Badge>
                      </div>
                      <div className="mt-2 text-[11px] text-muted-foreground line-clamp-2">{a.ai_reasoning}</div>
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Job</div>
                        <div className="text-xs truncate font-medium">{a.job?.title}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{a.job?.client}</div>
                      </div>
                      <div className="mt-3">
                        <Select value={a.stage} onValueChange={(v) => move(a.id, v)}>
                          <SelectTrigger data-testid={ATS.stageSelect(a.id)} className="h-7 text-[11px] font-mono uppercase tracking-wider"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STAGES.map((st) => <SelectItem key={st.key} value={st.key} className="text-xs">{st.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <div className="text-xs text-muted-foreground italic p-3 text-center border border-dashed border-border rounded-md">Empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
